/**
 * Générateur — Il utilise les puissances d'exposants positifs ou négatifs pour
 * simplifier l'écriture des produits.
 * Attendu ID : 201 | Niveau : 3ème | Domaine : Nombres (d11, O1)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, séances A4S1-A4S2). Plan de cartes
 * auto-validé 2026-07-16 (mode autonome, journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « produit »    — produit répété → base + exposant (2 trous saisie)
 *   « regles »     — 10ᵃ×10ᵇ, 10ᵃ/10ᵇ, (10ᵃ)ᵇ → exposant (1 trou signé)
 *   « inverse »    — a⁻ⁿ → fraction ; (a/b)⁻ⁿ → fraction inversée (trous)
 *   « rebours »    — décimal → puissance de 2/5/10 à exposant négatif (2 trous)
 *   « signe »      — signe sans calculer (classer Positif/Négatif)
 *   « ecritures »  — chaîne 10⁻ⁿ = 1/10ⁿ = 1/N = décimal (trous)
 *   « completer »  — exposant manquant dans une égalité (1 trou signé)
 *   « vf »         — malentendus de l'exposant négatif (tableau V/F)
 *   « simplifier » — produits/quotients même base, exposants ± (1 trou)
 *   « exposant »   — bases ≠, même exposant → (a/b)ⁿ (QCM)
 *   « mixte »      — somme avec puissance (anti-réflexe : décimal exact)
 *
 * Conventions : puissances et fractions rendues en KaTeX (FORMATS_TANOED
 * §0.5bis), saisies ASCII signées (« -5 »), arithmétique exacte (jamais de
 * flottant en réponse).
 *
 * ⚠️ Un trou placé en EXPOSANT doit être protégé par la triple accolade
 * (`10^{{{-3}}}`) : KaTeX refuse un `^` suivi d'une commande sans groupe, et
 * le markup de slot en est une (« Got function '\htmlClass' … as superscript »).
 */

import {
    randomChoice, randInt, shuffle, tex,
    makeQcm, makeTexteTrou, makeVraiFaux, makeClasser,
} from '../../../utils';
import { puissFrag, fracPuissFrag, decFrag, dec10, rat, ratAdd, decExact, pow } from './nombres';

const ATTENDU_ID = 201;

const L_PRODUIT = 'Écrire un produit répété sous forme de puissance';
const L_REGLES = 'Appliquer les règles sur les puissances de 10';
const L_INVERSE = 'Écrire une puissance d\'exposant négatif en fraction';
const L_REBOURS = 'Écrire un décimal comme puissance à exposant négatif';
const L_SIGNE = 'Déterminer le signe d\'une puissance sans calculer';
const L_ECRITURES = 'Relier les écritures d\'une puissance de 10 négative';
const L_COMPLETER = 'Compléter l\'exposant manquant d\'une égalité';
const L_VF = 'Vrai ou faux : les pièges de l\'exposant négatif';
const L_SIMPLIFIER = 'Simplifier un produit ou un quotient de puissances';
const L_EXPOSANT = 'Multiplier des puissances de même exposant';
const L_MIXTE = 'Calculer une somme contenant une puissance';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const LETTRES = ['A', 'B', 'C', 'D', 'E'];

const pgcdLocal = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };

// ═════════ « produit » — b × b × … × b = b^n (2 trous) ═════════

const genProduitTrous = () => {
    let base; let n;
    do { base = randomChoice([2, 3, 5, 7, 10]); n = randInt(4, 8); } while (base === n);
    const lettre = randomChoice(LETTRES);
    const facteurs = tex(Array(n).fill(String(base)).join(' \\times '));
    const phrase = `$${lettre} = {{${base}}}^{{{${n}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${lettre} = ${facteurs}.</p>`
        + '<p><b>Écris ce produit sous la forme d\'une puissance.</b></p>',
        { ...opts(L_PRODUIT), mode: 'saisie' });
    payload._meta.params = { base, n };
    return payload;
};

// ═════════ « regles » — règles sur les puissances de 10 (1 trou signé) ═════════

const genReglesTrous = () => {
    const type = randomChoice(['mul', 'div', 'pow']);
    let gauche; let r;
    if (type === 'mul') {
        let a; let b;
        do { a = randInt(-9, 9); b = randInt(-9, 9); } while (a === 0 || b === 0 || a + b === 0);
        gauche = `${puissFrag(10, a)} \\times ${puissFrag(10, b)}`;
        r = a + b;
    } else if (type === 'div') {
        let a; let b;
        do { a = randInt(-8, 9); b = randInt(-8, 9); } while (a === 0 || b === 0 || a === b);
        gauche = `${puissFrag(10, a)} \\div ${puissFrag(10, b)}`;
        r = a - b;
    } else {
        let a; let b;
        do { a = randInt(-5, 5); b = randInt(2, 4); } while (a === 0);
        gauche = `\\left(${puissFrag(10, a)}\\right)^{${b}}`;
        r = a * b;
    }
    const phrase = `$${gauche} = 10^{{{${r}}}}$`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Donne le résultat sous la forme d\'une puissance de 10.</b> '
        + 'Écris l\'exposant avec son signe s\'il est négatif.</p>',
        { ...opts(L_REGLES), mode: 'saisie' });
    payload._meta.params = { type, r };
    return payload;
};

// ═════════ « inverse » — a⁻ⁿ = 1/aⁿ = 1/x ; (a/b)⁻ⁿ = bⁿ/aⁿ ═════════

const genInverseTrous = () => {
    if (randInt(0, 2) < 2) {
        // Famille entière : a⁻ⁿ = 1/aⁿ = 1/x (étape imposée, 1 trou).
        let a; let n; let v;
        do { a = randomChoice([2, 3, 4, 5, 6, 7, 9, 10]); n = randInt(1, 3); v = pow(a, n); } while (v > 1000 || v === a);
        // L'exposant de l'étape intermédiaire est LUI AUSSI un trou : la
        // conversion exposant négatif → fraction était déjà affichée, seul le
        // calcul de aⁿ restait à faire.
        const phrase = `$${puissFrag(a, -n)} = \\dfrac{1}{${a}^{{{${n}}}}} = \\dfrac{1}{{{${v}}}}$`;
        const payload = makeTexteTrou(phrase,
            '<p><b>Exprime sous la forme d\'une fraction :</b> complète le dénominateur.</p>',
            { ...opts(L_INVERSE), mode: 'saisie' });
        payload._meta.params = { a, n, v };
        return payload;
    }
    // Famille fraction : (a/b)⁻ⁿ = bⁿ/aⁿ (2 trous distincts car a ≠ b ;
    // a et b premiers entre eux → la réponse bⁿ/aⁿ est déjà irréductible,
    // pas d'écriture simplifiée concurrente).
    let a; let b;
    do { a = randInt(2, 7); b = randInt(2, 9); } while (a === b || pgcdLocal(a, b) !== 1);
    const n = randInt(1, 2);
    const num = pow(b, n); const den = pow(a, n);
    const phrase = `$${fracPuissFrag(a, b, -n)} = \\dfrac{{{${num}}}}{{{${den}}}}$`;
    const payload = makeTexteTrou(phrase,
        '<p>L\'inverse d\'une fraction élève la fraction inverse à la puissance.</p>'
        + '<p><b>Exprime sous la forme d\'une fraction.</b></p>',
        { ...opts(L_INVERSE), mode: 'saisie' });
    payload._meta.params = { a, b, n };
    return payload;
};

// ═════════ « rebours » — décimal → puissance de 2/5/10 (2 trous) ═════════

// Table exacte décimal ↔ (base, exposant négatif) — développements finis connus.
const REBOURS = [
    ['0,5', 2, -1], ['0,25', 2, -2], ['0,125', 2, -3], ['0,0625', 2, -4],
    ['0,03125', 2, -5], ['0,015625', 2, -6],
    ['0,2', 5, -1], ['0,04', 5, -2], ['0,008', 5, -3], ['0,0016', 5, -4],
    ['0,1', 10, -1], ['0,01', 10, -2], ['0,001', 10, -3], ['0,0001', 10, -4], ['0,00001', 10, -5],
];

const genReboursTrous = () => {
    const [v, base, e] = randomChoice(REBOURS);
    const lettre = randomChoice(LETTRES);
    const phrase = `$${lettre} = ${decFrag(v)} = {{${base}}}^{{{${e}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On veut écrire ${lettre} = ${tex(decFrag(v))} sous la forme d'une puissance de 2, de 5 ou de 10 à exposant négatif.</p>`
        + '<p><b>Complète la base et l\'exposant.</b> Écris l\'exposant avec son signe.</p>',
        { ...opts(L_REBOURS), mode: 'saisie' });
    payload._meta.params = { v, base, e };
    return payload;
};

// ═════════ « signe » — classer Positif / Négatif sans calculer ═════════

// Fabrique une écriture et son signe, par familles (la parité de l'exposant
// survit aux exposants négatifs ; le « − » devant la puissance l'emporte).
// `a` est fourni UNIQUE par item : deux familles distinctes à bases distinctes
// ne peuvent pas produire le même texte → items distincts par construction.
const itemSigne = (famille, a) => {
    const pair = randInt(1, 4) * 2;          // exposant pair
    const impair = randInt(1, 4) * 2 + 1;    // exposant impair
    // b = a+1 (a pair) ou a+1/a+2 (a impair) → fraction TOUJOURS irréductible
    const b = a + (a % 2 === 0 ? 1 : randomChoice([1, 2]));
    switch (famille) {
        case 'neg_pair': return tex(puissFrag(`(-${a})`, randomChoice([pair, -pair])));
        case 'neg_impair': return tex(puissFrag(`(-${a})`, randomChoice([impair, -impair])));
        case 'moins_devant': return tex(`-${puissFrag(a, randomChoice([pair, impair, -pair]))}`);
        case 'pos_negexp': return tex(puissFrag(a, -randInt(1, 9)));
        case 'frac_neg': return tex(fracPuissFrag(-a, b, randomChoice([pair, -pair])));
        case 'frac_neg_impair': return tex(fracPuissFrag(-a, b, randomChoice([impair, -impair])));
        default: return tex(puissFrag(a, pair));
    }
};

const genSigneClasser = () => {
    const bases = shuffle([2, 3, 4, 5, 6, 7, 8, 9]);   // une base par item, toutes distinctes
    const famillesPos = shuffle(['neg_pair', 'pos_negexp', 'frac_neg']).slice(0, 3);
    const famillesNeg = shuffle(['neg_impair', 'moins_devant', 'frac_neg_impair']).slice(0, 3);
    const positifs = famillesPos.map((f, i) => itemSigne(f, bases[i]));
    const negatifs = famillesNeg.map((f, i) => itemSigne(f, bases[3 + i]));
    const payload = makeClasser([
        { name: 'Positif', items: positifs },
        { name: 'Négatif', items: negatifs },
    ], '<p><b>Sans calculer, classe chaque nombre selon son signe.</b></p>', opts(L_SIGNE));
    payload._meta.params = { positifs, negatifs };
    return payload;
};

// ═════════ « ecritures » — chaîne d'égalités 10⁻ⁿ / 1/10ⁿ / 1/N / décimal ═════════

const genEcrituresTrous = () => {
    const n = randInt(2, 6);
    const N = pow(10, n);            // ≤ 1 000 000, saisi SANS espaces
    const d = dec10(-n);
    const lettre = randomChoice(LETTRES);   // variabilité d'habillage (phrase = empreinte)
    const dir = randomChoice(['depuis_puissance', 'depuis_decimal', 'depuis_fraction']);
    let phrase; let texte;
    // Le dénominateur entier se saisit SANS séparateur de milliers : l'énoncé le
    // dit en phrase, car « 1000 » et « 1 000 » sont deux écritures justes du
    // même nombre et le scoring n'en accepte qu'une (FORMATS_TANOED §0.1).
    const SANS_ESPACES = 'Écris l\'entier sans espaces.';
    if (dir === 'depuis_puissance') {
        phrase = `$${lettre} : ${puissFrag(10, -n)} = \\dfrac{1}{10^{{{${n}}}}} = \\dfrac{1}{{{${N}}}} = {{${d}}}$`;
        texte = `<p>On complète les quatre écritures de ${tex(puissFrag(10, -n))}.</p>`
            + `<p><b>Complète.</b> ${SANS_ESPACES}</p>`;
    } else if (dir === 'depuis_decimal') {
        phrase = `$${lettre} : ${decFrag(d)} = \\dfrac{1}{{{${N}}}} = 10^{{{-${n}}}}$`;
        texte = `<p>On écrit ${tex(decFrag(d))} en fraction puis en puissance de 10.</p>`
            + `<p><b>Complète.</b> ${SANS_ESPACES} Écris l'exposant avec son signe.</p>`;
    } else {
        phrase = `$${lettre} : \\dfrac{1}{${puissFrag(10, n)}} = 10^{{{-${n}}}} = {{${d}}}$`;
        texte = `<p>On écrit ${tex(`\\dfrac{1}{${puissFrag(10, n)}}`)} en puissance de 10 puis en écriture décimale.</p>`
            + '<p><b>Complète.</b> Écris l\'exposant avec son signe.</p>';
    }
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_ECRITURES), mode: 'saisie' });
    payload._meta.params = { n, dir };
    return payload;
};

// ═════════ « completer » — exposant manquant (1 trou signé) ═════════

const genCompleterTrous = () => {
    const base = randomChoice([2, 3, 5, 6, 7]);
    const type = randomChoice(['mul', 'div', 'pow', 'mul3']);
    let phrase = null;
    let x = 0;
    for (let essai = 0; essai < 40 && !phrase; essai++) {
        if (type === 'mul') {
            const m = randInt(-9, 9) || 3; const r = randInt(-9, 9) || -4;
            x = r - m;
            if (x !== 0) phrase = `$${puissFrag(base, m)} \\times ${base}^{{{${x}}}} = ${puissFrag(base, r)}$`;
        } else if (type === 'div') {
            const m = randInt(-9, 9) || 5; const r = randInt(-9, 9) || -2;
            x = r + m;
            if (x !== 0) phrase = `$${base}^{{{${x}}}} \\div ${puissFrag(base, m)} = ${puissFrag(base, r)}$`;
        } else if (type === 'pow') {
            const m = randomChoice([-4, -3, -2, 2, 3, 4]); const k = randomChoice([-3, -2, 2, 3]);
            x = k;
            phrase = `$\\left(${puissFrag(base, m)}\\right)^{{{${k}}}} = ${puissFrag(base, m * k)}$`;
        } else {
            const m = randInt(-6, 6) || 2; const r = randInt(-6, 8) || 7;
            x = r - m - 1;
            if (x !== 0) phrase = `$${puissFrag(base, m)} \\times ${base}^{{{${x}}}} \\times ${base} = ${puissFrag(base, r)}$`;
        }
    }
    // repli déterministe
    if (!phrase) { x = -5; phrase = `$${puissFrag(base, 10)} \\times ${base}^{{{${x}}}} = ${puissFrag(base, 5)}$`; }
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète l\'exposant manquant.</b> Écris-le avec son signe s\'il est négatif.</p>',
        { ...opts(L_COMPLETER), mode: 'saisie' });
    payload._meta.params = { base, type, x };
    return payload;
};

// ═════════ « vf » — les malentendus de l'exposant négatif ═════════

const genVfTableau = () => {
    const lignes = []; const bonnes = [];
    // 1. inverse vs opposé
    {
        const a = randomChoice([2, 3, 5, 10]); const n = randInt(2, 5);
        if (randInt(0, 1)) { lignes.push(`L'inverse de ${tex(puissFrag(a, n))} est ${tex(puissFrag(a, -n))}.`); bonnes.push(0); }
        else { lignes.push(`L'inverse de ${tex(puissFrag(a, n))} est ${tex(`-${puissFrag(a, n)}`)}.`); bonnes.push(1); }
    }
    // 2. signe d'une base négative à exposant pair négatif
    {
        const a = randInt(2, 9); const n = randInt(1, 3) * 2;
        if (randInt(0, 1)) { lignes.push(`${tex(puissFrag(`(-${a})`, -n))} est un nombre positif.`); bonnes.push(0); }
        else { lignes.push(`${tex(puissFrag(`(-${a})`, -n))} est un nombre négatif.`); bonnes.push(1); }
    }
    // 3. base positive, exposant négatif → positif quand même
    {
        const a = randInt(2, 9); const n = randInt(1, 6);
        if (randInt(0, 1)) { lignes.push(`${tex(puissFrag(a, -n))} est un nombre négatif.`); bonnes.push(1); }
        else { lignes.push(`${tex(puissFrag(a, -n))} est un nombre positif.`); bonnes.push(0); }
    }
    // 4. double vs carré : 10^(-2m) est le CARRÉ de 10^(-m), pas son double.
    {
        const m = randInt(2, 4);
        if (randInt(0, 1)) { lignes.push(`${tex(puissFrag(10, -2 * m))} est le double de ${tex(puissFrag(10, -m))}.`); bonnes.push(1); }
        else { lignes.push(`${tex(puissFrag(10, -2 * m))} est le carré de ${tex(puissFrag(10, -m))}.`); bonnes.push(0); }
    }
    // Garantir au moins un Vrai et un Faux (re-tirage simple ligne 3 sinon).
    if (!bonnes.includes(0)) { lignes[2] = lignes[2].replace('négatif', 'positif'); bonnes[2] = 0; }
    if (!bonnes.includes(1)) { lignes[2] = lignes[2].replace('positif', 'négatif'); bonnes[2] = 1; }
    const ordre = shuffle(lignes.map((_, i) => i));
    const payload = makeVraiFaux(ordre.map(i => lignes[i]), ordre.map(i => bonnes[i]),
        '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload._meta.params = { lignes, bonnes };
    return payload;
};

// ═════════ « simplifier » — produits/quotients même base (1 trou signé) ═════════

const genSimplifierTrous = () => {
    const famille = randomChoice(['pos', 'neg', 'lettre']);
    let baseAff; let m; let k; let opDiv;
    if (famille === 'lettre') {
        baseAff = randomChoice(['b', 'x', 'y']);
        m = randInt(-5, 5) || 2; k = randInt(-5, 5) || -3; opDiv = randInt(0, 1) === 1;
    } else {
        const a = randomChoice([2, 3, 4, 5, 7]);
        baseAff = famille === 'neg' ? `(-${a})` : String(a);
        m = randInt(-6, 6) || 3; k = randInt(-6, 6) || -2; opDiv = randInt(0, 1) === 1;
    }
    const r = opDiv ? m - k : m + k;
    if (r === 0 || r === m) return genSimplifierTrous();
    // L'erreur type (DIVISER les exposants au lieu de les soustraire, les
    // MULTIPLIER au lieu de les additionner) ne doit pas donner la bonne
    // réponse : 3⁴ ÷ 3² = 3² alors que 4 ÷ 2 = 2 aussi.
    if (opDiv && k !== 0 && m / k === r) return genSimplifierTrous();
    if (!opDiv && m * k === r) return genSimplifierTrous();
    const gauche = `${puissFrag(baseAff, m)} ${opDiv ? '\\div' : '\\times'} ${puissFrag(baseAff, k)}`;
    const phrase = `$${gauche} = ${baseAff}^{{{${r}}}}$`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Écris sous la forme d\'une seule puissance :</b> complète l\'exposant, '
        + 'avec son signe s\'il est négatif.</p>',
        { ...opts(L_SIMPLIFIER), mode: 'saisie' });
    payload._meta.params = { baseAff, m, k, opDiv, r };
    return payload;
};

// ═════════ « exposant » — bases différentes, même exposant (QCM) ═════════

const genExposantQcm = () => {
    // a^n × b^(-n) = (a/b)^n avec a/b irréductible (a, b premiers entre eux).
    const paires = [[5, 3], [3, 2], [7, 4], [5, 2], [4, 3], [9, 2], [7, 5]];
    const [a, b] = randomChoice(paires);
    const n = randomChoice([2, 3, 4, 5]);
    const enonce = tex(`${puissFrag(a, n)} \\times ${puissFrag(b, -n)}`);
    const correct = tex(fracPuissFrag(a, b, n));
    const distracteurs = [
        tex(fracPuissFrag(b, a, n)),        // fraction inversée
        tex(fracPuissFrag(a, b, -n)),       // exposant du mauvais signe
        tex(puissFrag(a * b, n)),           // on multiplie les bases
    ];
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p><b>Écris ${enonce} sous la forme d'une seule puissance.</b></p>`,
        opts(L_EXPOSANT));
    payload._meta.params = { a, b, n };
    return payload;
};

// ═════════ « mixte » — somme avec puissance (décimal exact) ═════════

const genMixteTrous = () => {
    // a⁻ⁿ a un développement décimal FINI (base 2, 4, 5 ou 10) ; on ajoute un
    // entier : les règles des puissances ne s'appliquent PAS aux sommes.
    const bases = [[2, 3], [2, 2], [4, 2], [5, 2], [5, 1], [10, 2], [10, 3], [2, 1], [4, 1]];
    const [a, n] = randomChoice(bases);
    const k = randInt(2, 19);
    const somme = ratAdd(rat(1, pow(a, n)), rat(k, 1));
    const res = decExact(somme);
    const phrase = `$${puissFrag(a, -n)} + ${k} = {{${res}}}$`;
    const payload = makeTexteTrou(phrase,
        '<p>Attention : les règles des puissances ne concernent pas les sommes.</p>'
        + '<p><b>Calcule et donne le résultat en écriture décimale</b>, avec une virgule.</p>',
        { ...opts(L_MIXTE), mode: 'saisie' });
    payload._meta.params = { a, n, k, res };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    produit_texte_trou_saisie: genProduitTrous,
    regles_texte_trou_saisie: genReglesTrous,
    inverse_texte_trou_saisie: genInverseTrous,
    rebours_texte_trou_saisie: genReboursTrous,
    signe_classer: genSigneClasser,
    ecritures_texte_trou_saisie: genEcrituresTrous,
    completer_texte_trou_saisie: genCompleterTrous,
    vf_tableau_vf: genVfTableau,
    simplifier_texte_trou_saisie: genSimplifierTrous,
    exposant_qcm_unique: genExposantQcm,
    mixte_texte_trou_saisie: genMixteTrous,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous', tableau_vf: 'Vrai/Faux',
    classer: 'Classer',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-03 (chantier groupes-differencies) ; sert aux exercices complexes du groupe Fort.
export const CATEGORIES = [
    { id: 'produit', label: L_PRODUIT, etapes: 1, formatsList: buildFormats('produit') },
    { id: 'regles', label: L_REGLES, etapes: 2, formatsList: buildFormats('regles') },
    { id: 'inverse', label: L_INVERSE, etapes: 2, formatsList: buildFormats('inverse') },
    { id: 'rebours', label: L_REBOURS, etapes: 3, formatsList: buildFormats('rebours') },
    { id: 'signe', label: L_SIGNE, etapes: 2, formatsList: buildFormats('signe') },
    { id: 'ecritures', label: L_ECRITURES, etapes: 3, formatsList: buildFormats('ecritures') },
    { id: 'completer', label: L_COMPLETER, etapes: 3, formatsList: buildFormats('completer') },
    { id: 'vf', label: L_VF, etapes: 2, formatsList: buildFormats('vf') },
    { id: 'simplifier', label: L_SIMPLIFIER, etapes: 2, formatsList: buildFormats('simplifier') },
    { id: 'exposant', label: L_EXPOSANT, etapes: 3, formatsList: buildFormats('exposant') },
    { id: 'mixte', label: L_MIXTE, etapes: 3, formatsList: buildFormats('mixte') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
